<template>
    <section class="ds-schedule-vacancies">
        <button
            type="button"
            class="ds-schedule-vacancies-header"
            :aria-expanded="isOpen"
            @click="isOpen = !isOpen"
        >
            <span>{{ headerTitle }}</span>
            <icon class="ds-schedule-vacancies-chevron" :class="{ 'ds-schedule-vacancies-chevron-open': isOpen }" width="9" height="13">
                <IconChevronRight />
            </icon>
        </button>

        <div v-if="isOpen" class="ds-schedule-vacancies-row">
            <div class="ds-schedule-vacancies-label">
                <span v-for="channel in channels" :key="channel.key">{{ channelLabel(channel) }}</span>
            </div>
            <div class="ds-schedule-vacancies-days">
                <div
                    v-for="(vacanciesForDay, index) in vacanciesByDay"
                    :key="index"
                    class="ds-schedule-vacancies-cell"
                    :class="{
                        'ds-schedule-vacancies-cell-today': isTodayCell(index),
                    }"
                >
                    <template v-if="vacanciesForDay.length === 1">
                        <div class="ds-schedule-vacancies-times">
                            <span>{{ formatTime(vacanciesForDay[0].dttm_work_start_at) }}</span>
                            <span>{{ formatTime(vacanciesForDay[0].dttm_work_end_at) }}</span>
                        </div>
                        <span class="ds-schedule-vacancies-position">
                            {{ vacanciesForDay[0].position.name }}
                        </span>
                    </template>
                    <div v-else-if="vacanciesForDay.length > 1" class="ds-schedule-vacancies-count">
                        <div>{{ vacanciesForDay.length }}</div>
                        <div>смены</div>
                    </div>
                    <button
                        v-else-if="canCreateVacancy && dataState === 'loaded'"
                        type="button"
                        class="ds-schedule-vacancies-create"
                        :aria-label="`Создать вакансию: ${cellDates[index]}`"
                        @click="emit('create', { day: dayjs(cellDates[index]).date(), date: cellDates[index], channel: 'inner' })"
                    >
                        <icon width="21" height="21"><IconPlus /></icon>
                    </button>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from 'vue'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import IconChevronRight from '@/ui/icons/IconChevronRight.vue'
import IconPlus from '@/ui/icons/IconPlus.vue'
import { getMyVacancies } from '@/api/exchange'
import { useAuthStore } from '@/stores/authStore'
import { useScheduleStore } from '@/stores/scheduleStore'

dayjs.extend(utc)

type VacancyChannel = 'outer' | 'inner'
type DataState = 'idle' | 'loading' | 'loaded' | 'error'
const MAX_VACANCY_PAGES = 1000

interface Vacancy {
    id: string
    date: string
    dttm_work_start_at: string
    dttm_work_end_at: string
    status: string
    inner_exchange: boolean
    outer_exchange: boolean
    position: {
        id: string
        name: string
    }
}

interface Props {
    days: number
    today: string
    // Недельный вид: вакансии загружаются и считаются за открытые даты.
    dates?: string[]
}

const { days, today, dates } = defineProps<Props>()
const emit = defineEmits<{
    create: [data: { day: number; date: string; channel: VacancyChannel }]
}>()

const authStore = useAuthStore()
const scheduleStore = useScheduleStore()
const isOpen = ref(false)
const dataState = ref<DataState>('idle')
const vacancies = ref<Vacancy[]>([])
let requestId = 0

const channels: { key: VacancyChannel; label: string }[] = [
    { key: 'outer', label: 'Аутсорс' },
    { key: 'inner', label: 'Внутренняя биржа' },
]

const currentDepartmentId = computed(() => scheduleStore.currentDepartment?.id)
const cellDates = computed(() => dates ?? Array.from({ length: days }, (_, index) => dateForDay(index + 1)))
const currentMonthStart = computed(() => cellDates.value[0])
const currentMonthEnd = computed(() => cellDates.value[cellDates.value.length - 1])
const canCreateVacancy = computed(() => Boolean(authStore.permissions?.can_create_vacancy_exchange))

const channelVacancies = computed(() => ({
    outer: vacancies.value.filter(vacancy => vacancy.outer_exchange),
    inner: vacancies.value.filter(vacancy => vacancy.inner_exchange),
}))

const vacanciesByDate = computed<Record<string, Vacancy[]>>(() => {
    const result: Record<string, Vacancy[]> = {}

    for (const vacancy of vacancies.value) {
        if (vacancy.outer_exchange) (result[vacancy.date] ||= []).push(vacancy)
        if (vacancy.inner_exchange) (result[vacancy.date] ||= []).push(vacancy)
    }

    return result
})

const vacanciesByDay = computed(() => cellDates.value.map(date => vacanciesByDate.value[date] || []))
const isTodayCell = (index: number) => dates
    ? cellDates.value[index] === dayjs().format('YYYY-MM-DD')
    : index + 1 === +today && scheduleStore.currentScheduleDate.format('MM') === dayjs().format('MM')

const headerTitle = computed(() => {
    if (dataState.value !== 'loaded') return 'Открытые вакансии'

    return `Открытые вакансии: ${channelVacancies.value.outer.length + channelVacancies.value.inner.length}`
})

const channelLabel = (channel: { key: VacancyChannel; label: string }) => {
    if (dataState.value !== 'loaded') return channel.label

    return `${channel.label}: ${channelVacancies.value[channel.key].length} смен`
}

const dateForDay = (day: number) => scheduleStore.currentScheduleDate.date(day).format('YYYY-MM-DD')

const formatTime = (value: string) => dayjs.utc(value).format('HH:mm')

async function loadVacancies() {
    const department = currentDepartmentId.value
    const currentRequestId = ++requestId

    vacancies.value = []
    if (!department) {
        dataState.value = 'idle'
        return
    }

    dataState.value = 'loading'
    const result: Vacancy[] = []
    let page = 1
    let next: string | null = null

    try {
        do {
            if (page > MAX_VACANCY_PAGES) {
                if (currentRequestId !== requestId) return

                vacancies.value = []
                dataState.value = 'error'
                return
            }

            const response = await getMyVacancies({
                department,
                dt_from: currentMonthStart.value,
                dt_to: currentMonthEnd.value,
                status: 'open',
                page_size: 100,
                page,
            })
            const body = response.data.body
            result.push(...body.results)
            next = body.next
            page += 1
        } while (next)

        if (currentRequestId !== requestId) return

        vacancies.value = result.filter(vacancy => vacancy.status === 'open')
        dataState.value = 'loaded'
    } catch (_error) {
        if (currentRequestId !== requestId) return

        vacancies.value = []
        dataState.value = 'error'
    }
}

watch([currentDepartmentId, currentMonthStart], loadVacancies, { immediate: true })

onUnmounted(() => {
    requestId += 1
})

defineExpose({
    reload: loadVacancies,
})
</script>
